(ns hai.ui.components.form.input
  (:require
   [hai.ui.components :as c]
   [hai.ui.components.form.core :as core]
   [hai.ui.utils.validate :refer [validate]]
   [humbleai.belt.dom :as dom]
   [humbleai.belt.malli.transform.default-value :as default-value]
   [humbleai.belt.malli.transform.strip-extra-keys :as strip-extra-keys]
   [humbleai.belt.malli.transform.translate-keys :as translate-keys]
   [lambdaisland.ornament :as o]
   [malli.core :as m]
   [malli.transform :as mt]
   [malli.util :as mu]
   [reagent.impl.template :as reagent.template]))

(def radix
  {:input.render/input c/TextFieldRoot})

(o/defstyled no-styling-input :input
  {:background "none"
   :border "none"}
  [:&:focus-visible {:outline "none"}])

(def no-styling
  {:input.render/input no-styling-input})

(defn style
  [render-attr]
  (comp render-attr :input.render/style))

(def Render
  [:map
   [:input.render/style
    {:optional true
     ::translate-keys/from :style}
    map?]

   [:input.render/input
    {::translate-keys/from :input
     ::default-value/fn (style :input.render/input)}
    [:fn (fn [render-fn]
           (or (ifn? render-fn)
               (instance? reagent.template/NativeWrapper render-fn)))]]])

;; TODO move all input schema here from core
(def Input
  (mu/merge
   core/BaseField
   [:map
    [:input/render
     {:default {:style radix}
      ::translate-keys/from :render}
     Render]
    [:form.field.input/on-key-press
     {:optional true
      ::translate-keys/from :on-key-press}
     ifn?]
    [:form.field.input/on-key-down
     {:optional true
      ::translate-keys/from :on-key-down}
     ifn?]
    [:form.field.input/auto-focus?
     {:optional true
      ::translate-keys/from :auto-focus?}
     :boolean]]))

(def decode-props
  (m/decoder Input (mt/transformer
                    strip-extra-keys/transformer
                    default-value/transformer
                    translate-keys/transformer)))

(defn field
  "Input field using uncontrolled input pattern to preserve cursor position.
   Uses :default-value with a key based on form-setup-id to handle resets.
   See: https://cljdoc.org/d/reagent/reagent/1.2.0/doc/tutorials/controlled-inputs"
  [{:keys [style] :as props} & children]
  (let [{:as props
         {input-render :input.render/input} :input/render
         :form.field/keys [loading? placeholder set-value! on-focus current-value form-setup-id]
         :form.field.input/keys [on-key-press on-key-down auto-focus?]}
        (decode-props props)
        on-change (comp set-value! dom/->value)]
    (validate Input props)
    ;; `^{:key ...}` on a function-call form (rather than a literal vector) only tags the source
    ;; code at read time, not the vector `into` returns, so Reagent never actually saw a `:key` here
    ;; and the "remount when form-setup-id changes" this was meant to do never fired. `with-meta` on
    ;; the actual returned vector is what Reagent's hiccup conversion looks at.
    (with-meta
      (into [input-render
             {:type "text"
              :style style
              :auto-focus auto-focus?
              :on-change on-change
              :on-focus on-focus
              :on-key-press on-key-press
              :on-key-down on-key-down
              :placeholder placeholder
              :default-value (or current-value "")}
             ;; Input loading indicator, pass in `false` and `true` to toggle without changing the width
             ;; of the input.
             (when (some? loading?)
               (if loading?
                 [c/TextFieldSlot
                  {:side "right"}
                  [c/Spinner]]
                 [c/TextFieldSlot
                  {:side "right"}
                  [:div {:style {:width "16px"}}]]))]
            children)
      {:key form-setup-id})))

(defn textarea
  [props & children]
  (let [{:as props
         :form.field/keys [placeholder set-value! current-value]}
        (decode-props props)]
    (validate Input props)
    (let [on-change (comp set-value! dom/->value)]
      (fn [_]
        (into [c/TextArea
               {:on-change on-change
                :placeholder placeholder
                :default-value current-value}]
              children)))))

(o/defstyled expanding-textarea :div
  ;; [:& input]
  [:>.grow-wrap :grid
   [:&:after :invisible :whitespace-pre-wrap
    {;; TODO this is to work around that we're using Radix inputs and should be removed once we're
     ;; using unthemed components
     :--text-area-border-width "1px"
     :--text-area-padding-y "calc(var(--space-1) * 1.5)"
     :padding-top "var(--text-area-padding-y)"
     :padding-bottom "var(--text-area-padding-y)"
     :padding-left "var(--space-2)"
     :padding-right "var(--space-2)"
     :font-size "var(--font-size-2)"
     :line-height "var(--line-height-2)"
     :letter-spacing "var(--letter-spacing-2)"}
    {:content "attr(data-content) ' '"}]
   [:&>.rt-TextAreaRoot :resize-none :overflow-hidden]
   [#{:&:after :&>.rt-TextAreaRoot } {:grid-area "1 / 1 / 2 / 2"}]]
  ([{:as props :form.field/keys [current-value]} & children]
   [:div.grow-wrap
    {:data-content current-value}
    (into [textarea props] children)]))

(o/defstyled auto-expanding :div
  [:>.grow-wrap :grid
   [:&:after :invisible :whitespace-pre-wrap
    {:content "attr(data-content) ' '"}]
   [:&>* :resize-none :overflow-hidden]
   [#{:&:after :&>* } {:grid-area "1 / 1 / 2 / 2"}]]
  ([{:as props :keys [value]} & children]
   (into [:div.grow-wrap {:data-content value}]
         children)))

(defn controlled-field
  [props & children]
  (let [{:as props
         {input-render :input.render/input} :input/render
         :form.field/keys [loading? placeholder set-value! current-value]}
        (decode-props props)]
    (validate Input props)
    (let [on-change (comp set-value! dom/->value)]
      (fn [_]
        (into [input-render
               {:type "text"
                :on-change on-change
                :placeholder placeholder
                :value (or current-value "")}
               ;; Input loading indicator, pass in `false` and `true` to toggle without changing the width
               ;; of the input.
               (when (not (nil? loading?))
                 (if loading?
                   [c/TextFieldSlot
                    {:side "right"}
                    [c/Spinner]]
                   [c/TextFieldSlot
                    {:side "right"}
                    [:div {:style {:width "16px"}}]]))]
              children)))))
